import { useEffect, useRef } from 'react';

type UseLikeUseEffect = (effect: Parameters<typeof useEffect>[0], dep: any) => ReturnType<typeof useEffect>;

/**
 * Works as useEffect with one dependency, but:
 *  - allows dep to be so called "unnecessary" dependency (not present in effect function).
 *  - allows effect to use so called "missing" dependencies (variables not present in dependency array).
 *  - guarantees that "missing" dependencies used by effect are actual.
 *  - allows to omit effect from dependency array
 *
 * Should be used only after trying this: https://reactjs.org/docs/hooks-faq.html#what-can-i-do-if-my-effect-dependencies-change-too-often
 *
 * Note that value passed as dep should be primitive or memoized.
 * To pass array of values, memoize it first!
 *
 * @param {function} effect Callback to run.
 * @param {any} dep Primitive or memoized value.
 * @return {void}
 */
export const useEffectWithSingleDependency: UseLikeUseEffect = (effect, dep) => {
    const effectRef = useRef(effect);
    effectRef.current = effect;
    useEffect(() => effectRef.current(), [dep]);
};
